import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { techniciansApi, ApiError } from "../lib/api";
import { useConfirmGate } from "../lib/useConfirm";
import {
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
} from "../components/ui-modern";

/**
 * The New Technician screen — see docs/FRONTEND_BACKLOG.md. Replaces the
 * inline "Add a Technician" card TechniciansListPageModern.tsx used to
 * keep at the top of the list (removed in favor of a dedicated page,
 * matching the "+ New customer"/"+ New work order" pattern those two list
 * pages already use) — centered like NewCustomerPageModern.tsx/
 * EditCustomerPageModern.tsx (`mx-auto max-w-xl`), with the same field set
 * as TechnicianEditPageModern.tsx (Name + Code) rather than just the name
 * field the old inline form had, since `techniciansApi.create` already
 * accepts `code` too.
 */
export function NewTechnicianPageModern() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const confirmGate = useConfirmGate();

  const createMutation = useMutation({
    mutationFn: () => techniciansApi.create({ displayName, code: code.trim() || undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["technicians"] });
      navigate("/technicians");
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Could not add this technician."),
  });

  return (
    <div className="mx-auto max-w-xl">
      <ModernBreadcrumbs items={[{ label: "Technicians", to: "/technicians" }, { label: "Add Technician" }]} />
      <h1 className="m-0 mb-4 text-[27px] font-bold text-[#071a33]">New Technician</h1>

      <ModernCard>
        {error && <ModernErrorBanner message={error} />}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmGate.request(() => createMutation.mutate());
          }}
        >
          <ModernField label="Name" htmlFor="displayName" required>
            <ModernInput id="displayName" required autoFocus uppercase value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </ModernField>
          <ModernField label="Code" htmlFor="code">
            <ModernInput id="code" uppercase value={code} onChange={(e) => setCode(e.target.value)} />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Optional short code or badge number</p>
          </ModernField>
          <div className="flex gap-2.5">
            <ModernButton type="submit" disabled={createMutation.isPending || !displayName.trim()}>
              {createMutation.isPending ? "Saving…" : "Save Technician"}
            </ModernButton>
            <ModernButton type="button" variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </ModernButton>
          </div>
        </form>
      </ModernCard>

      <ModernConfirmDialog {...confirmGate.dialogProps} message="Add this technician?" />
    </div>
  );
}
